import { useState, type ReactNode } from "react";
import { MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import type { SalveazaTargetareAmbiguitateRequest } from "@/api/types/targetareConfig";
import {
  ETICHETE_AMBIGUITATE,
  ETICHETE_REGULA,
  REGULI_INCADRARE,
  TIPURI_AMBIGUITATE,
} from "@/utils/targetareConfig";
import { formatNumar } from "@/utils/targetareFormatting";
import type { RandAmbiguitate } from "@/utils/targetareRaportScoruri";
import type { ColoanaTabel } from "@/utils/targetareTabel";
import type { FirmeCriteriuDeschis } from "../FirmeCriteriuDialog";

export type ContextAmbiguitate = {
  onSalveaza: (patch: SalveazaTargetareAmbiguitateRequest) => void;
  onFirme: (deschis: FirmeCriteriuDeschis) => void;
  inCurs: boolean;
};

const opreste = (e: { stopPropagation: () => void }) => e.stopPropagation();
const NEDEFINIT = "(fără)";

function Justificare({
  rand,
  onSalveaza,
}: {
  rand: RandAmbiguitate;
  onSalveaza: (text: string) => void;
}) {
  const [text, setText] = useState(rand.justificare ?? "");
  return (
    <Popover onOpenChange={(v) => v && setText(rand.justificare ?? "")}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex max-w-[300px] items-center gap-1 truncate rounded px-1 text-left text-xs text-muted-foreground hover:bg-muted"
          title={rand.justificare ?? "Adaugă justificarea"}
          onClick={opreste}
        >
          <MessageSquare className="h-3 w-3 shrink-0" />
          <span className="truncate">{rand.justificare ?? "justificare…"}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[460px] space-y-2"
        onClick={opreste}
      >
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={5}
        />
        <Button size="sm" className="w-full" onClick={() => onSalveaza(text)}>
          Salvează justificarea
        </Button>
      </PopoverContent>
    </Popover>
  );
}

function ButonFirme({
  total,
  scop,
  onClick,
}: {
  total: number;
  scop: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="tabular-nums hover:underline disabled:no-underline"
      disabled={total === 0}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      title="Deschide firmele"
    >
      {formatNumar(total)}
      <span className="text-muted-foreground"> ({formatNumar(scop)})</span>
    </button>
  );
}

export function celulaAmbiguitate(ctx: ContextAmbiguitate) {
  return (
    coloana: ColoanaTabel<RandAmbiguitate>,
    r: RandAmbiguitate
  ): ReactNode => {
    const v = r[coloana.id];
    const criteriu = (fel: "principal" | "secundar" | "lista") => ({
      criteriu: { tip: "serviciu" as const, entity_id: r.entity_id, fel },
      titlu: `${r.name}: firme potrivite ${fel === "principal" ? "prin CAEN principal" : fel === "secundar" ? "prin cod autorizat" : "de pe liste"}`,
    });
    switch (coloana.id) {
      case "entity_id":
        return <span className="font-mono text-xs">{r.entity_id}</span>;
      case "type_text":
        return (
          <div onClick={opreste}>
            <Select
              value={r.type ?? NEDEFINIT}
              disabled={ctx.inCurs}
              onValueChange={(t) =>
                ctx.onSalveaza({ entity_id: r.entity_id, type: t })
              }
            >
              <SelectTrigger className="h-7 w-[150px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {r.type === null && (
                  <SelectItem value={NEDEFINIT}>Nedefinit</SelectItem>
                )}
                {TIPURI_AMBIGUITATE.map((t) => (
                  <SelectItem key={t} value={t}>
                    {ETICHETE_AMBIGUITATE[t]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      case "super_ambiguu":
        return (
          <div onClick={opreste}>
            <Switch
              checked={r.super_ambiguu}
              disabled={ctx.inCurs}
              onCheckedChange={(val) =>
                ctx.onSalveaza({ entity_id: r.entity_id, super_ambiguu: val })
              }
            />
          </div>
        );
      case "regula_text":
        return (
          <div onClick={opreste}>
            <Select
              value={r.regula_tip ?? ""}
              disabled={ctx.inCurs}
              onValueChange={(t) =>
                ctx.onSalveaza({ entity_id: r.entity_id, regula_tip: t })
              }
            >
              <SelectTrigger
                className="h-7 w-[250px] text-xs"
                title={ETICHETE_REGULA[r.regula_tip ?? ""]}
              >
                <SelectValue placeholder="fără regulă" />
              </SelectTrigger>
              <SelectContent>
                {REGULI_INCADRARE.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}: {ETICHETE_REGULA[t]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      case "orice_dimensiune":
        return (
          <div onClick={opreste}>
            <Switch
              checked={r.orice_dimensiune}
              disabled={ctx.inCurs}
              onCheckedChange={(val) =>
                ctx.onSalveaza({
                  entity_id: r.entity_id,
                  orice_dimensiune: val,
                })
              }
            />
          </div>
        );
      case "justificare":
        return (
          <Justificare
            rand={r}
            onSalveaza={(text) =>
              ctx.onSalveaza({ entity_id: r.entity_id, justificare: text })
            }
          />
        );
      case "firme_principal":
        return (
          <ButonFirme
            total={r.firme_principal}
            scop={r.firme_principal_scop}
            onClick={() => ctx.onFirme(criteriu("principal"))}
          />
        );
      case "firme_secundar":
        return (
          <ButonFirme
            total={r.firme_secundar}
            scop={r.firme_secundar_scop}
            onClick={() => ctx.onFirme(criteriu("secundar"))}
          />
        );
      case "firme_lista":
        return (
          <ButonFirme
            total={r.firme_lista}
            scop={r.firme_lista_scop}
            onClick={() => ctx.onFirme(criteriu("lista"))}
          />
        );
      default:
        break;
    }
    if (typeof v === "boolean") return v ? "da" : "nu";
    if (typeof v === "number")
      return <span className="tabular-nums">{formatNumar(v)}</span>;
    return (
      <span title={String(v ?? "")} className="block truncate">
        {String(v ?? "")}
      </span>
    );
  };
}
